import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Check, ArrowRight, LogOut } from "lucide-react";

const TIERS = [
  { key: "standard_assessment", name: "Standard Assessment", price: "$1,500",
    meta: "6-month lookback · up to 3 vendors / channels",
    features: ["Marketing spend reconciliation", "Vendor invoice & performance review", "Lead and retained-case analysis", "Cost per retained case where data allows", "Executive findings report"] },
  { key: "expanded_assessment", name: "Expanded Assessment", price: "$2,500",
    meta: "12-month lookback · up to 5 vendors / channels", popular: true,
    features: ["Everything in Standard", "Full 12-month historical review", "Deeper vendor comparisons", "Lead-to-retained-case conversion analysis", "Expanded findings & recommendations"] },
];

export default function Subscribe() {
  const { user, logout } = useAuth();
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");

  const choose = async (lookup_key) => {
    setError("");
    setBusy(lookup_key);
    try {
      const { data } = await api.post("/payments/checkout", { lookup_key, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      setBusy("");
    }
  };

  return (
    <div className="dark min-h-screen bg-[#0A1B2E] text-white font-body flex flex-col" data-testid="subscribe-page">
      <div className="flex items-center justify-between px-8 py-6 border-b border-white/10">
        <div className="flex items-center gap-2.5 font-body font-bold text-xl">
          <img src="/retainproof-icon-dark.png" alt="RetainProof" className="h-7 w-auto" />
          <span>Retain<span className="text-[#0E7C7B]">Proof</span></span>
        </div>
        <button onClick={logout} data-testid="subscribe-logout" className="flex items-center gap-2 text-sm text-zinc-400 hover:text-white transition-colors">
          <LogOut className="h-4 w-4" /> Sign out
        </button>
      </div>

      <div className="flex-1 flex flex-col items-center justify-center p-6">
        <div className="text-center max-w-2xl mb-10">
          <p className="font-mono-data text-xs uppercase tracking-[0.2em] text-[#D5A44A]">Marketing Performance Assessment</p>
          <h1 className="font-display text-4xl font-light mt-2">Activate {user?.firm_name || "your firm"}</h1>
          <p className="text-zinc-400 text-sm mt-3">Start a one-time Marketing Performance Assessment to access your firm's workspace and findings. Choose the assessment that matches the size of your marketing program.</p>
        </div>

        {error && (
          <div className="mb-6 border border-red-500/40 bg-red-500/10 text-red-300 text-sm p-3 max-w-md" data-testid="subscribe-error">{error}</div>
        )}

        <div className="grid sm:grid-cols-2 gap-6 w-full max-w-3xl">
          {TIERS.map((p) => (
            <div key={p.key} className={`relative border p-7 flex flex-col ${p.popular ? "border-[#D5A44A] bg-[#14120A]" : "border-white/15 bg-white/5"}`} data-testid={`subscribe-plan-${p.key}`}>
              {p.popular && <span className="absolute -top-3 left-7 bg-[#D5A44A] text-black text-[10px] font-mono-data uppercase tracking-widest px-2 py-1">Most popular</span>}
              <h2 className="font-display text-2xl">{p.name}</h2>
              <p className="mt-1 font-mono-data text-[10px] uppercase tracking-widest text-[#0E7C7B]">{p.meta}</p>
              <p className="mt-3"><span className="font-display text-3xl">{p.price}</span><span className="text-zinc-500 text-sm"> · one time</span></p>
              <ul className="mt-5 space-y-2 flex-1">
                {p.features.map((f) => (
                  <li key={f} className="flex gap-2 text-sm text-zinc-300"><Check className="h-4 w-4 text-[#0E7C7B] shrink-0 mt-0.5" />{f}</li>
                ))}
              </ul>
              <Button onClick={() => choose(p.key)} disabled={!!busy} data-testid={`subscribe-choose-${p.key}`}
                className="mt-6 w-full bg-[#D5A44A] text-black hover:bg-[#B8923D] rounded-none h-11 font-medium">
                {busy === p.key ? "Redirecting…" : <>Start Your Assessment <ArrowRight className="ml-2 h-4 w-4" /></>}
              </Button>
            </div>
          ))}
        </div>
        <p className="text-zinc-500 text-sm mt-8">Need a Comprehensive, Enterprise, or ongoing engagement? <a href="/consultation" className="text-[#D5A44A] hover:underline">Talk to our team</a>.</p>
      </div>
    </div>
  );
}
